// src/utils/scoringUtils.js
import { normalizeText, tokenizeName, levenshteinDistance, checkTransliterationPatterns } from './textUtils';
import { detectCulturalPattern } from './culturalPatterns';

/**
 * Calculate similarity score between component and search tokens
 */
export const calculateComponentSimilarity = (component, searchTokens) => {
  if (!component || !searchTokens || searchTokens.length === 0) return 0;
  
  let bestScore = 0;
  const normalizedComponent = normalizeText(component);
  
  for (const searchToken of searchTokens) {
    // Exact match
    if (normalizedComponent === searchToken) {
      return 1.0;
    }
    
    // Calculate Levenshtein distance for fuzzy matching
    const distance = levenshteinDistance(normalizedComponent, searchToken);
    const maxLength = Math.max(normalizedComponent.length, searchToken.length);
    
    // Convert distance to similarity (0-1)
    let similarityScore;
    
    if (maxLength <= 3) {
      // For very short strings, only accept exact matches
      similarityScore = distance === 0 ? 1.0 : 0;
    } else if (maxLength <= 6) {
      // For short strings, be more strict
      similarityScore = distance === 0 ? 1.0 : 
                       distance === 1 ? 0.7 : 
                       distance === 2 ? 0.3 : 0;
    } else {
      // For longer strings, use scaled similarity
      similarityScore = Math.max(0, 1 - (distance / (maxLength * 0.7)));
    }
    
    // Only give containment bonus for longer strings
    if (normalizedComponent.length > 3 && searchToken.length > 3 &&
        (normalizedComponent.includes(searchToken) || searchToken.includes(normalizedComponent))) {
      const containmentBonus = 0.15;
      similarityScore = Math.min(1.0, similarityScore + containmentBonus);
    }
    
    bestScore = Math.max(bestScore, similarityScore);
  }
  
  return bestScore;
};

/**
 * Assess positional correctness between name and search tokens
 */
export const assessPositionalCorrectness = (nameTokens, searchTokens) => {
  if (!nameTokens || !searchTokens || 
      nameTokens.length === 0 || searchTokens.length === 0) {
    return 0;
  }
  
  // If only 1 token in search, position doesn't matter
  if (searchTokens.length === 1) return 1.0;
  
  // Check if major tokens are in correct position
  let correctPositions = 0;
  const matchedPositions = new Set();
  
  // For each search token, find its best position in name tokens
  for (let i = 0; i < searchTokens.length; i++) {
    const searchToken = searchTokens[i];
    
    let bestMatch = { position: -1, score: 0 };
    
    // Find best matching name token
    for (let j = 0; j < nameTokens.length; j++) {
      if (matchedPositions.has(j)) continue; // Skip already matched positions
      
      const nameToken = nameTokens[j];
      const similarity = calculateComponentSimilarity(nameToken, [searchToken]);
      
      if (similarity > bestMatch.score) {
        bestMatch = { position: j, score: similarity };
      }
    }
    
    // If we found a match
    if (bestMatch.position !== -1 && bestMatch.score > 0.5) {
      matchedPositions.add(bestMatch.position);
      
      // Check if positions are relatively consistent
      if (Math.abs(bestMatch.position - i) <= 1) {
        correctPositions++;
      }
    }
  }
  
  // Calculate position score (0-1)
  return searchTokens.length > 0 ? correctPositions / searchTokens.length : 0;
};

/**
 * Check if all search tokens are contained in the full name
 */
export const checkAllTokensPresent = (nameTokens, searchTermTokens) => {
  if (!nameTokens || !searchTermTokens || 
      nameTokens.length === 0 || searchTermTokens.length === 0) {
    return { allPresent: false, matchedTokens: [] };
  }
  
  // Track which search tokens are found
  const matchedTokens = [];
  const matchedPositions = new Set();
  
  // For each search token, try to find a match in the name tokens
  for (const searchToken of searchTermTokens) {
    // RELAXED MATCHING: Use more lenient matching for distributed matches
    // Check for exact match first
    const exactMatch = nameTokens.some(nameToken => 
      normalizeText(nameToken) === normalizeText(searchToken)
    );
    
    if (exactMatch) {
      matchedTokens.push({
        token: searchToken,
        matchType: 'exact',
        position: nameTokens.findIndex(nameToken => 
          normalizeText(nameToken) === normalizeText(searchToken)
        )
      });
      matchedPositions.add(matchedTokens[matchedTokens.length - 1].position);
      continue;
    }
    
    // Check for fuzzy/partial match with lower threshold
    let bestFuzzyMatch = { score: 0, position: -1 };
    
    for (let i = 0; i < nameTokens.length; i++) {
      // Allow fuzzy matching on positions that had exact matches since
      // different search tokens might match the same name token
      if (matchedPositions.has(i)) continue;
      
      const nameToken = nameTokens[i];
      const similarity = calculateComponentSimilarity(nameToken, [searchToken]);
      
      // Use 0.7 threshold for distributed match detection (more lenient)
      if (similarity > 0.7 && similarity > bestFuzzyMatch.score) {
        bestFuzzyMatch = { score: similarity, position: i };
      }
    }
    
    if (bestFuzzyMatch.position !== -1) {
      matchedTokens.push({
        token: searchToken,
        matchType: 'fuzzy',
        position: bestFuzzyMatch.position,
        score: bestFuzzyMatch.score
      });
      matchedPositions.add(bestFuzzyMatch.position);
    }
    
    // If we still didn't find a match, try containment matching
    if (!matchedTokens.find(t => t.token === searchToken)) {
      for (let i = 0; i < nameTokens.length; i++) {
        // Allow containment matching on any position as a last resort
        const nameToken = normalizeText(nameTokens[i]);
        const searchTerm = normalizeText(searchToken);
        
        if ((nameToken.length > 2 && searchTerm.length > 2) &&
            (nameToken.includes(searchTerm) || searchTerm.includes(nameToken))) {
          matchedTokens.push({
            token: searchToken,
            matchType: 'containment',
            position: i
          });
          break;
        }
      }
    }
  }
  
  return {
    allPresent: matchedTokens.length === searchTermTokens.length,
    matchedTokens
  };
};

/**
 * Calculate minimum token match threshold
 */
export const calculateMinimumTokenThreshold = (nameTokens, searchTermTokens, score) => {
  // Count how many search tokens have a meaningful match
  let matchedTokenCount = 0;
  
  for (const searchToken of searchTermTokens) {
    for (const nameToken of nameTokens) {
      const similarity = calculateComponentSimilarity(nameToken, [searchToken]);
      if (similarity > 0.6) { 
        matchedTokenCount++;
        break;
      }
    }
  }
  
  // Calculate percentage of matched tokens
  const matchPercentage = searchTermTokens.length > 0 ? 
    matchedTokenCount / searchTermTokens.length : 0;
  
  // If we matched less than half the tokens, severely penalize the score
  if (matchPercentage < 0.5) {
    return Math.max(0, score - 50); 
  }
  
  // If more than half but not all, apply moderate penalty
  if (matchPercentage < 1.0) {
    return Math.max(0, score - (20 * (1 - matchPercentage))); 
  }
  
  return score;
};

/**
 * Check for distributed matches across name variants
 */
export const checkDistributedMatches = (entityData, searchTermTokens) => {
  // Skip if only a single token search or no names
  if (searchTermTokens.length <= 1 || !entityData.names || entityData.names.length <= 1) {
    return { isDistributedMatch: false, distribution: null };
  }
  
  // Track which search tokens match each name variant
  const matchedTokensByName = {};
  const allMatchedTokens = new Set();
  
  // For each name variant, check which search tokens match
  entityData.names.forEach((name, nameIndex) => {
    // Get full name - ALWAYS use wholeName if available
    const fullName = name.wholeName || '';
    if (!fullName) return;
    
    // Tokenize name
    const nameTokens = tokenizeName(fullName);
    
    // Check which search tokens match this name
    const matchedTokens = [];
    
    searchTermTokens.forEach(searchToken => {
      // RELAXED MATCHING: Use more lenient matching for distributed matches
      // Check for exact match first
      const exactMatch = nameTokens.some(nameToken => 
        normalizeText(nameToken) === normalizeText(searchToken)
      );
      
      if (exactMatch) {
        matchedTokens.push(searchToken);
        allMatchedTokens.add(searchToken);
        return;
      }
      
      // Check for fuzzy/partial match with lower threshold
      for (const nameToken of nameTokens) {
        // For short tokens, require exact match (already checked above)
        if (nameToken.length <= 3 || searchToken.length <= 3) continue;
        
        // For longer tokens, use similarity with lower threshold
        const similarity = calculateComponentSimilarity(nameToken, [searchToken]);
        
        // Use 0.5 threshold for distributed match detection (more lenient)
        if (similarity >= 0.5) {
          matchedTokens.push(searchToken);
          allMatchedTokens.add(searchToken);
          return;
        }
        
        // Check for containment as well (more lenient for distributed matches)
        const normNameToken = normalizeText(nameToken);
        const normSearchToken = normalizeText(searchToken);
        
        if ((normNameToken.length > 3 && normSearchToken.length > 3) &&
            (normNameToken.includes(normSearchToken) || normSearchToken.includes(normNameToken))) {
          matchedTokens.push(searchToken);
          allMatchedTokens.add(searchToken);
          return;
        }
      }
    });
    
    if (matchedTokens.length > 0) {
      matchedTokensByName[nameIndex] = matchedTokens;
    }
  });
  
  // Check if we have a distributed match (tokens spread across variants)
  const totalMatchedTokens = allMatchedTokens.size;
  let isAnyNameComplete = false;
  
  // Check if any single name contains all matched tokens
  Object.values(matchedTokensByName).forEach(tokens => {
    if (tokens.length === totalMatchedTokens) {
      isAnyNameComplete = true;
    }
  });
  
  // It's a distributed match if:
  // 1. We matched at least half of the search tokens
  // 2. Tokens are spread across multiple name variants
  // 3. No single name variant contains all matched tokens
  const isDistributedMatch = 
    totalMatchedTokens > 0 &&
    totalMatchedTokens >= Math.ceil(searchTermTokens.length / 2) && // At least half the tokens match
    Object.keys(matchedTokensByName).length > 1 &&
    !isAnyNameComplete;
  
  return {
    isDistributedMatch,
    distribution: isDistributedMatch ? matchedTokensByName : null,
    matchedTokens: [...allMatchedTokens]
  };
};

/**
 * Calculate unified match score on 0-100 scale
 */
export const calculateUnifiedScore = (nameEntry, searchTermTokens, searchTermNormalized) => {
  // Use wholeName as the primary source of truth
  const fullName = nameEntry.fullName || '';
  const normalizedName = nameEntry.normalizedName || '';
  
  // Detect cultural pattern
  const culturalPattern = detectCulturalPattern(nameEntry);
  
  // Tokenize name for analysis
  const nameTokens = tokenizeName(fullName);
  
  // Check if all search tokens are contained in the full name
  const tokenPresenceResult = checkAllTokensPresent(nameTokens, searchTermTokens);
  
  // Exact match checks
  const exactMatchFound = (
    normalizedName === searchTermNormalized || 
    normalizeText(fullName) === searchTermNormalized ||
    normalizedName.replace(/\s+/g, ' ') === searchTermNormalized.replace(/\s+/g, ' ')
  );

  // If we found an exact match or all tokens are present, boost the scoring
  if (exactMatchFound || tokenPresenceResult.allPresent) {
    // Scoring distribution:
    // 1. Token Match Quality (0-50)
    // 2. Positional Correctness (0-25)
    // 3. Transliteration Handling (0-25)
    
    // 1. Token Match Quality (0-50)
    let tokenMatchScore = 0;
    
    // Calculate token match quality using the entire name
    const tokensWithScores = searchTermTokens.map(searchToken => {
      let bestScore = 0;
      let bestMatchedToken = '';
      
      // Find best matching token in name
      for (const nameToken of nameTokens) {
        const similarity = calculateComponentSimilarity(nameToken, [searchToken]);
        if (similarity > bestScore) {
          bestScore = similarity;
          bestMatchedToken = nameToken;
        }
      }
      
      return { token: searchToken, score: bestScore, matchedToken: bestMatchedToken };
    });
    
    // Get average token match score
    const avgTokenScore = tokensWithScores.reduce((sum, item) => sum + item.score, 0) / 
                        (tokensWithScores.length || 1);
    
    // Scale to 0-50 range
    tokenMatchScore = Math.round(avgTokenScore * 50);
    
    // 2. Positional Correctness (0-25)
    let positionalScore = 0;
    
    // Calculate positional correctness
    const positionalCorrectness = assessPositionalCorrectness(nameTokens, searchTermTokens);
    positionalScore = Math.round(positionalCorrectness * 25);
    
    // 3. Transliteration Handling (0-25)
    let transliterationScore = 0;

    // If exact match found, maximize all scores
    if (exactMatchFound) {
      tokenMatchScore = 50;
      positionalScore = 25;
      transliterationScore = 25;
      
      // Add special flag
      nameEntry.exactMatch = true;
    } else {
      // For token matches, be more generous with positional scoring
      if (tokenPresenceResult.allPresent) {
        // Boost positional score if all tokens are present but not in order
        positionalScore = Math.max(positionalScore, 15);
        transliterationScore = 20;
        
        // Add token match flag
        nameEntry.allTokensPresent = true;
      }
      
      // Regular transliteration scoring logic
      const transliterationSimilarity = checkTransliterationPatterns(normalizedName, searchTermNormalized);
      transliterationScore = Math.max(transliterationScore, Math.round(transliterationSimilarity * 25));
    }
    
    // Calculate final score (maximum 100)
    const finalScore = tokenMatchScore + positionalScore + transliterationScore;
    
    // Minimum score guarantee when all tokens are present
    let adjustedScore = finalScore;
    if (tokenPresenceResult.allPresent) {
      adjustedScore = Math.max(adjustedScore, 60); // Ensure at least Medium confidence
      
      // Special boost for cases like "Chun Choe" matching "Chun Yong Choe"
      if (searchTermTokens.length >= 2 && 
          nameTokens.length === searchTermTokens.length + 1) {
        // This is exactly the case where we have one extra token in the middle
        adjustedScore = Math.max(adjustedScore, 75); // Ensure at least High confidence
      }
    }
    
    // Add special boost for distributed match tokens (partial matches)
    if (nameEntry.isDistributedMatch && nameEntry.matchedTokens) {
      // For distributed matches, give extra weight to partial matches
      // of the tokens that are actually matched in this name variant
      const matchedTokensInThisVariant = nameEntry.distributionInfo[nameEntry.nameIndex] || [];
      const partialMatchWeight = matchedTokensInThisVariant.length / searchTermTokens.length;
      
      // Apply a weighted boost based on how many tokens match in this variant
      adjustedScore = Math.max(adjustedScore, 45 + (partialMatchWeight * 30));
    }
    
    // Store scores for UI display
    nameEntry.tokenMatchScore = tokenMatchScore;
    nameEntry.positionalScore = positionalScore;
    nameEntry.transliterationScore = transliterationScore;
    nameEntry.culturalPattern = culturalPattern;
    nameEntry.tokenPresenceInfo = tokenPresenceResult;
    
    // Handle distributed matches
    if (nameEntry.isDistributedMatch) {
      // Apply a distributed match boost (previously in quality adjustments)
      const distributedBoost = 15; // Make this a bit higher than before
      adjustedScore = Math.min(100, adjustedScore + distributedBoost);
      
      // Track the boost for UI display
      nameEntry.distributedBoost = distributedBoost;
      
      // Update confidence level if necessary
      if (adjustedScore >= 95) {
        nameEntry.confidenceLevel = "Very High";
      } else if (adjustedScore >= 80) {
        nameEntry.confidenceLevel = "High";
      } else if (adjustedScore >= 65) {
        nameEntry.confidenceLevel = "Medium";
      }
    }
    
    // Return final score capped at 100
    return Math.min(Math.max(0, Math.round(adjustedScore)), 100);
  } else {
    // Not all tokens are present - apply revised scoring
    
    // 1. Token Match Quality (0-50)
    let tokenMatchScore = 0;
    
    // Calculate token match quality
    const tokensWithScores = searchTermTokens.map(searchToken => {
      let bestScore = 0;
      
      for (const nameToken of nameTokens) {
        const similarity = calculateComponentSimilarity(nameToken, [searchToken]);
        bestScore = Math.max(bestScore, similarity);
      }
      
      return { token: searchToken, score: bestScore };
    });
    
    // Get average token match score
    const avgTokenScore = tokensWithScores.reduce((sum, item) => sum + item.score, 0) / 
                        (tokensWithScores.length || 1);
    
    // Scale to 0-50 range
    tokenMatchScore = Math.round(avgTokenScore * 50);
    
    // 2. Positional Correctness (0-25)
    let positionalScore = 0;
    
    // Calculate positional correctness
    const positionalCorrectness = assessPositionalCorrectness(nameTokens, searchTermTokens);
    positionalScore = Math.round(positionalCorrectness * 25);
    
    // 3. Transliteration Handling (0-25)
    let transliterationScore = 0;
    
    // Regular transliteration scoring logic
    const transliterationSimilarity = checkTransliterationPatterns(normalizedName, searchTermNormalized);
    transliterationScore = Math.round(transliterationSimilarity * 25);
    
    // Check if full name contains all search tokens (but not full exact match)
    const containsAllTokens = searchTermTokens.every(token => 
      nameTokens.some(nameToken => 
        nameToken === token || nameToken.includes(token) || token.includes(nameToken)
      )
    );
    
    if (containsAllTokens) {
      transliterationScore = Math.max(transliterationScore, 15);
    }
    
    // Calculate final score
    let finalScore = tokenMatchScore + positionalScore + transliterationScore;
    
    // Apply minimum token threshold check - MODIFIED to directly adjust final score
    finalScore = calculateMinimumTokenThreshold(nameTokens, searchTermTokens, finalScore);
    
    // Store scores for UI display
    nameEntry.tokenMatchScore = tokenMatchScore;
    nameEntry.positionalScore = positionalScore;
    nameEntry.transliterationScore = transliterationScore;
    nameEntry.culturalPattern = culturalPattern;
    nameEntry.tokenPresenceInfo = tokenPresenceResult;
    
    // Handle distributed matches
    if (nameEntry.isDistributedMatch) {
      // Apply a distributed match boost (previously in quality adjustments)
      const distributedBoost = 15; // Make this a bit higher than before
      finalScore = Math.min(100, finalScore + distributedBoost);
      
      // Track the boost for UI display
      nameEntry.distributedBoost = distributedBoost;
      
      // Update confidence level if necessary
      if (finalScore >= 95) {
        nameEntry.confidenceLevel = "Very High";
      } else if (finalScore >= 80) {
        nameEntry.confidenceLevel = "High";
      } else if (finalScore >= 65) {
        nameEntry.confidenceLevel = "Medium";
      }
    }
    
    // Return final score capped at 100
    return Math.min(Math.max(0, Math.round(finalScore)), 100);
  }
};
